import { Component, OnDestroy, OnInit, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../../services/i18n/language';
import { GuestSessionService } from '../../../services/guest-session';
import { PhoneClaimService } from '../../../services/phone-claim';

/** Окно «введите телефон» для гостя: открывается из закрытых разделов, после ввода логин гостя становится телефоном. */
@Component({
  selector: 'app-phone-claim-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './phone-claim-modal.component.html',
  styleUrl: './phone-claim-modal.component.scss'
})
export class PhoneClaimModalComponent implements OnInit, OnDestroy {
  nameInput = '';
  phoneInput = '+7';

  @ViewChild('phoneField') phoneField?: ElementRef<HTMLInputElement>;

  /** Что откроется после ввода имени и телефона. */
  readonly benefits = [
    { key: 'phoneClaim.benefitFreight', fallback: 'Прогноз стоимости перевозки' },
    { key: 'phoneClaim.benefitPayments', fallback: 'Таможенные платежи по каждому товару' },
    { key: 'phoneClaim.benefitPermits', fallback: 'Разрешительные документы для ваших товаров' },
    { key: 'phoneClaim.benefitInvoice', fallback: 'Цифровой инвойс с кодами ТН ВЭД' },
    { key: 'phoneClaim.benefitSaved', fallback: 'Расчёт сохранится в «Моих расчётах» — вернётесь к нему в любой момент' }
  ];
  error: string | null = null;
  saving = false;

  private subscriptions = new Subscription();

  constructor(
    public claim: PhoneClaimService,
    private guestSession: GuestSessionService,
    private languageService: LanguageService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.claim.changed$.subscribe(() => {
        if (this.claim.visible) {
          this.nameInput = '';
          this.phoneInput = '+7';
          this.error = null;
          this.saving = false;
        }
        this.cdr.markForCheck();
      })
    );
    this.subscriptions.add(this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck()));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  close(): void {
    if (this.saving) return;
    this.claim.close();
  }

  focusPhone(): void {
    this.phoneField?.nativeElement.focus();
  }

  confirm(): void {
    const name = this.nameInput.trim();
    if (name.length < 2) {
      this.error = this.t('phoneClaim.nameError', 'Введите имя');
      return;
    }
    const digits = this.phoneInput.replace(/\D/g, '');
    if (digits.length < 11) {
      this.error = this.t('phoneClaim.error', 'Введите номер телефона');
      return;
    }
    this.saving = true;
    this.error = null;
    this.subscriptions.add(
      this.guestSession.attachPhone('+' + digits, name).subscribe({
        next: () => {
          const target = this.claim.redirectTo;
          this.saving = false;
          this.claim.complete();
          if (target) {
            this.router.navigateByUrl(target);
          }
          this.cdr.markForCheck();
        },
        error: err => {
          this.saving = false;
          this.error = err?.error?.message && err.status === 400
            ? err.error.message
            : this.t('phoneClaim.failed', 'Не удалось сохранить номер. Попробуйте ещё раз');
          this.cdr.markForCheck();
        }
      })
    );
  }

  /** Живая маска телефона: +7-702-444-48-81 */
  onPhoneInput(value: string): void {
    let digits = (value || '').replace(/\D/g, '');
    if (digits.startsWith('8')) digits = '7' + digits.slice(1);
    if (!digits.startsWith('7')) digits = '7' + digits;
    digits = digits.slice(0, 11);

    const rest = digits.slice(1);
    let formatted = '+7';
    if (rest.length > 0) formatted += '-' + rest.slice(0, 3);
    if (rest.length > 3) formatted += '-' + rest.slice(3, 6);
    if (rest.length > 6) formatted += '-' + rest.slice(6, 8);
    if (rest.length > 8) formatted += '-' + rest.slice(8, 10);
    this.phoneInput = formatted;
  }

  /** Запрещает ввод чего-либо, кроме цифр. */
  onPhoneKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End', 'Enter'];
    if (allowed.includes(event.key) || event.ctrlKey || event.metaKey) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  onPhonePaste(event: ClipboardEvent): void {
    event.preventDefault();
    this.onPhoneInput(event.clipboardData?.getData('text') || '');
  }
}
